import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useCustomTabs } from '../../hooks/useCustomTabs';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../ConfirmationModal';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
`;

const ModalContent = styled.div`
    background: #2c2c2e;
    padding: 24px;
    border-radius: 12px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
    color: #fff;
`;

const ModalHeader = styled.h3`
    margin-top: 0;
    margin-bottom: 16px;
`;

const InputGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        color: #b0b0b0;
    }

    input {
        width: 100%;
        padding: 10px;
        border-radius: 6px;
        border: 1px solid #444;
        background: #3a3a3c;
        color: #fff;
        font-size: 16px;

        &:focus {
            outline: none;
            border-color: #007aff;
        }
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
`;

const Button = styled.button`
    padding: 10px 20px;
    border-radius: 6px;
    border: none;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &.primary {
        background-color: #007aff;
        color: #fff;

        &:hover {
            background-color: #0056b3;
        }
    }

    &.secondary {
        background-color: #555;
        color: #fff;

        &:hover {
            background-color: #666;
        }
    }

    &.danger {
        background-color: #dc3545;
        color: #fff;

        &:hover {
            background-color: #c82333;
        }
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    padding: 8px;
    background: #3a3a3c;
    border-radius: 6px;
    min-height: 38px;
`;

const TagBadge = styled.span`
    display: flex;
    align-items: center;
    background-color: #555;
    color: #fff;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 14px;
    cursor: pointer;

    button {
        background: none;
        border: none;
        color: #fff;
        margin-left: 8px;
        cursor: pointer;
        padding: 0;
        font-size: 16px;
    }
`;

const AvailableTagsContainer = styled(TagsContainer)`
    background: none;
    padding: 0;
    margin-top: 16px;
`;

const TabSettingsModal = ({ isOpen, onClose, tab, allSaves, moveSaveToTab, deleteTab, setViewMode, allUniqueTags }) => {
    const { user } = useAuth();
    const { updateTabConfig } = useCustomTabs(user?.uid);
    const [autoAddTags, setAutoAddTags] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    useEffect(() => {
        if (tab) {
            setAutoAddTags(tab.autoAddTags || []);
        }
    }, [tab]);

    const handleAddTag = (tag) => {
        if (tag && !autoAddTags.includes(tag)) {
            setAutoAddTags([...autoAddTags, tag]);
        }
        setNewTag('');
    };

    const handleRemoveTag = (tagToRemove) => {
        setAutoAddTags(autoAddTags.filter(tag => tag !== tagToRemove));
    };

    const handleSave = async () => {
        if (!tab) return;
        await updateTabConfig(tab.id, { autoAddTags });
        onClose();
    };

    const handleDelete = () => {
        setIsConfirmModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!tab) return;

        const savesInTab = allSaves.filter(save => save.tabId === tab.id);
        for (const save of savesInTab) {
            await moveSaveToTab(save.id, null);
        }

        await deleteTab(tab.id);
        setIsConfirmModalOpen(false);
        onClose();
        setViewMode('active');
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>Settings for "{tab?.name}"</ModalHeader>
                <InputGroup>
                    <label htmlFor="autoAddTags">Auto-add saves with these tags:</label>
                    <TagsContainer>
                        {autoAddTags.map(tag => (
                            <TagBadge key={tag}>
                                {tag}
                                <button onClick={() => handleRemoveTag(tag)}>&times;</button>
                            </TagBadge>
                        ))}
                    </TagsContainer>
                    <input
                        id="autoAddTags"
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTag(newTag.trim())}
                        placeholder="Type a tag and press Enter"
                        style={{ marginTop: '8px' }}
                    />
                </InputGroup>

                {allUniqueTags && allUniqueTags.length > 0 && (
                    <InputGroup>
                        <label>Or select from existing tags:</label>
                        <AvailableTagsContainer>
                            {allUniqueTags.map(tag => (
                                <TagBadge key={tag.name} onClick={() => handleAddTag(tag.name)}>
                                    {tag.name}
                                </TagBadge>
                            ))}
                        </AvailableTagsContainer>
                    </InputGroup>
                )}

                <ButtonGroup>
                    <Button className="danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>Delete Tab</Button>
                    <Button className="secondary" onClick={onClose}>Cancel</Button>
                    <Button className="primary" onClick={handleSave}>Save</Button>
                </ButtonGroup>
            </ModalContent>
            {isConfirmModalOpen && (
                <ConfirmationModal
                    onCancel={() => setIsConfirmModalOpen(false)}
                    onConfirm={confirmDelete}
                    title="Delete Tab"
                    message={`Are you sure you want to delete the tab "${tab?.name}"? All saves within this tab will be moved to the main list.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            )}
        </ModalOverlay>
    );
};

export default TabSettingsModal;
